import { Navigate, Route } from 'react-router-dom'

import { AppDownloadPage } from '@/components/AppDownloadPage/AppDownloadPage'
import { SupportImpersonation } from '@/components/business/support-impersonation'
import Clients from '@/components/Clients/Clients'
import { RoleGuard } from '@/components/role-guard'
import Surveys from '@/components/Surveys/Surveys'
import { BetaPage } from '@/pages/beta'
import { ClientProfilePage, ClientsPage, ClientsTablePage } from '@/pages/clients'
import { AnnouncementDetails } from '@/pages/communication/announcement-details'
import { EmailRecipientsTable } from '@/pages/communication/announcement-details/email-recipients-table'
import { NotificationRecipientsTable } from '@/pages/communication/announcement-details/notification-recipients-table'
import { SmsRecipientsTable } from '@/pages/communication/announcement-details/sms-recipients-table'
import { AnnouncementsPage } from '@/pages/communication/announcements'
import { SurveyDetails } from '@/pages/communication/survey-details'
import { SurveysPage } from '@/pages/communication/surveys'
import { BookingTypesPage } from '@/pages/organisation/booking-types'
import { ManagersPage } from '@/pages/organisation/managers'
import { PlaceDetailsPage, PlacesPage } from '@/pages/organisation/places'
import { PropertyManagersPage } from '@/pages/organisation/property-managers'
import { ServicesPage } from '@/pages/organisation/services'
import { StaffPage } from '@/pages/organisation/staff'
import { ReservationsPage } from '@/pages/reservations'
import { AmenitiesTab } from '@/pages/reservations/amenities'
import { NewReservationsGuard } from '@/pages/reservations/new-reservations-guard'
import { ReservationDetailsPage } from '@/pages/reservations/reservation-details'
import { ReservationsDashboardTab } from '@/pages/reservations/reservations-dashboard'
import { TimeBlocksTab } from '@/pages/reservations/time-blocks'
import {
  LeaseDetailsPage,
  UnitChecklistsPage,
  UnitDetailsPage,
  UnitGeneralPage,
  UnitLeasesPage,
  UnitMaintenancePage,
  UnitsPage,
} from '@/pages/units'

import CONFIG from '../../brandingConfig'
import Attendees from '../../components/Attendees/Attendees'
import Booking from '../../components/Booking/Booking'
import BuildingAnalytics from '../../components/BuildingAnalytics/BuildingAnalytics'
import Calendar from '../../components/Calendar/Calendar'
import Can, { ADMIN_OR_PM } from '../../components/Can'
import Chat from '../../components/Chat/Chat'
import ClientReservationsAmenities from '../../components/ClientReservations/Amenities/ClientReservationsAmenities'
import ClientReservations from '../../components/ClientReservations/ClientReservations'
import ClientReservationsMyBookings from '../../components/ClientReservations/MyBookings/ClientReservationsMyBookings'
import { ClientDetailsPage } from '../../components/Clients/ClientProfileView/ClientsDetailsPage'
import CustomerPortal from '../../components/CustomerPortal/CustomerPortal'
import Dashboard from '../../components/Dashboard'
import AppointmentFormPage from '../../components/EventManagement/appointment/AppointmentFormPage'
import ClassFormPage from '../../components/EventManagement/class/ClassFormPage'
import BuidlingEventFormPage from '../../components/EventManagement/event/EventFormPage'
import ReminderFormPage from '../../components/EventManagement/reminder/ReminderFormPage'
import Integrations from '../../components/Integrations/Integrations'
import NotificationsPortal from '../../components/NotificationsPortal/NotificationsPortal'
import OnCallSchedule from '../../components/OnCallSchedule/OnCallSchedule'
import Organisation from '../../components/Organisation/Organisation'
import OrganisationBooking from '../../components/Organisation/OrganisationBooking/OrganisationBooking'
import OrganisationManagers from '../../components/Organisation/OrganisationManagers/OrganisationManagers'
import OrganisationPlaces from '../../components/Organisation/OrganisationPlaces/OrganisationPlaces'
import OrganisationProducts from '../../components/Organisation/OrganisationProducts/OrganisationProducts'
import OrganisationPromoCodes from '../../components/Organisation/OrganisationPromoCodes/OrganisationPromoCodes'
import OrganisationServices from '../../components/Organisation/OrganisationServices/OrganisationServices'
import OrganisationStaff from '../../components/Organisation/OrganisationStaff/OrganisationStaff'
import Payments from '../../components/Organisation/Payments/Payments'
import PropertyManagersLegacyPage from '../../components/Organisation/PropertyManagers/PropertyManagersPage'
import ReputationManagement from '../../components/Organisation/ReputationManagement/ReputationManagement'
import Protect from '../../components/Protect'
import ProtectDashboardPage from '../../components/ProtectDashboardPage'
import ResidentEvents from '../../components/ResidentEvents/ResidentEvents'
import Store from '../../components/Store/Store'
import StoreCategory from '../../components/Store/StoreCategory'
import StoreProduct from '../../components/Store/StoreProduct'
import Upselling from '../../components/Upselling/Upselling'
import UserClasses from '../../components/UserPanel/UserClasses'
import UserNotifications from '../../components/UserPanel/UserNotifications'
import UserPanel from '../../components/UserPanel/UserPanel'
import UserPassword from '../../components/UserPanel/UserPassword'
import UserProfile from '../../components/UserPanel/UserProfile'
import withRoleProtection from '../../components/withRoleProtection'
import { buildingDashboardRoutesConfig } from './building-dashboard.routes'

// TODO: Replace, it should not be in routes config file
const ProtectedIntegrations = withRoleProtection(
  Integrations,
  ['PropertyManager'],
  '/dashboard/calendar',
)
const ProtectedResidentEvents = withRoleProtection(
  ResidentEvents,
  ['PropertyManager'],
  '/dashboard/calendar',
)
const ProtectedCustomerPortal = withRoleProtection(
  CustomerPortal,
  ['Administrator', 'PropertyManager'],
  '/dashboard/calendar',
)
const ProtectedReservationsPage = withRoleProtection(
  ReservationsPage,
  [...ADMIN_OR_PM, 'ShiftManager'],
  '/dashboard/calendar',
)
const ProtectedAmenitiesTab = withRoleProtection(AmenitiesTab, [...ADMIN_OR_PM], '/reservations')

export const dashboardRoutesConfig = () => (
  <Route path="/dashboard" element={<Protect component={Dashboard} />}>
    <Route index element={<Navigate replace to="building-analytics" />} />
    <Route
      path="building-analytics"
      element={<ProtectDashboardPage component={BuildingAnalytics} />}
    />

    <Route
      path="calendar"
      element={
        <Can to="see" on="Calendar">
          <Calendar />
        </Can>
      }
    />
    <Route path="calendar/:eventId/attendees" element={<Attendees />} />

    <Route path="calendar/appointment/:eventId?" element={<AppointmentFormPage />} />
    <Route path="calendar/class/:eventId?" element={<ClassFormPage />} />
    <Route path="calendar/reminder/:eventId?" element={<ReminderFormPage />} />
    <Route path="calendar/event/:eventId?" element={<BuidlingEventFormPage />} />

    <Route path="on-call-schedule" element={<OnCallSchedule />} />
    <Route path="nportal" element={<NotificationsPortal />} />

    {/* Client Reservations */}
    <Route path="client-reservations" element={<ClientReservations />}>
      <Route path="amenities" element={<ClientReservationsAmenities />} />
      <Route path="my-bookings" element={<ClientReservationsMyBookings />} />
    </Route>

    {buildingDashboardRoutesConfig()}

    <Route path="chat" element={<Chat />} />

    <Route path="clients/legacy" element={<Clients />} />
    <Route path="clients" element={<ClientsPage />}>
      <Route index element={<ClientsTablePage />} />
    </Route>
    <Route path="clients/:clientId" element={<ClientProfilePage />} />
    <Route path="clients/:clientId/legacy" element={<ClientDetailsPage />} />

    <Route path="announcements" element={<AnnouncementsPage />} />

    <Route path="units">
      <Route index element={<UnitsPage />} />
      <Route path=":unitId" element={<UnitDetailsPage />}>
        <Route index element={<Navigate replace to="general" />} />
        <Route path="general" element={<UnitGeneralPage />} />
        <Route path="leases" element={<UnitLeasesPage />} />
        <Route path="leases/:leaseId" element={<LeaseDetailsPage />} />
        <Route path="checklists" element={<UnitChecklistsPage />} />
        <Route path="maintenance" element={<UnitMaintenancePage />} />
      </Route>
    </Route>

    <Route path="survey/legacy" element={<Surveys />} />
    <Route path="survey" element={<SurveysPage />} />
    <Route path="survey/:id" element={<SurveyDetails />} />

    <Route path="announcements/:id" element={<AnnouncementDetails />}>
      <Route path="email" element={<EmailRecipientsTable />} />
      <Route path="sms" element={<SmsRecipientsTable />} />
      <Route path="notification" element={<NotificationRecipientsTable />} />
    </Route>

    {CONFIG?.booking && <Route path="booking" element={<Booking />} />}

    <Route path="payments" element={<Payments />} />

    <Route path="store" element={<Store />}>
      {/* @ts-ignore – this route is old component without TS */}
      <Route path="category/:name" element={<StoreCategory />}>
        <Route path="product/:product" element={<StoreProduct />} />
      </Route>

      <Route path="reputation-management" element={<ReputationManagement />} />
    </Route>

    <Route path="user-panel" element={<UserPanel />}>
      <Route index element={<Navigate replace to="classes" />} />
      <Route path="classes" element={<UserClasses />} />
      <Route
        path="profile"
        element={
          <Can on={'UserProfile'}>
            <UserProfile />
          </Can>
        }
      />
      <Route path="password" element={<UserPassword />} />
      <Route path="notifications" element={<UserNotifications />} />
      <Route path="impersonation" element={<SupportImpersonation />} />
    </Route>

    <Route path="organisation" element={<Organisation />}>
      <Route index element={<Navigate replace to="staff" />} />

      <Route path="staff">
        <Route index element={<StaffPage />} />
        <Route path="legacy">
          <Route index element={<OrganisationStaff />} />
          <Route path=":trainerId" element={<OrganisationStaff />} />
        </Route>
      </Route>

      <Route
        path="managers"
        element={
          <RoleGuard allowedRoles={['Administrator']} redirectTo="/dashboard/organisation" />
        }
      >
        <Route index element={<ManagersPage />} />
        <Route path="legacy">
          <Route index element={<OrganisationManagers />} />
          <Route path=":managerId" element={<OrganisationManagers />} />
        </Route>
      </Route>

      <Route path="property-managers">
        <Route index element={<PropertyManagersPage />} />
        <Route path="legacy">
          <Route index element={<PropertyManagersLegacyPage />} />
          <Route path=":propertyManagerId" element={<PropertyManagersLegacyPage />} />
        </Route>
      </Route>

      <Route path="services">
        <Route index element={<ServicesPage />} />
        <Route path="legacy">
          <Route index element={<OrganisationServices />} />
          <Route path=":serviceId">
            <Route index element={<OrganisationServices />} />
            <Route path="sku/:skuId" element={<OrganisationServices />} />
            <Route path=":mode" element={<OrganisationServices />} />
          </Route>
        </Route>
      </Route>

      <Route path="products">
        <Route index element={<OrganisationProducts />} />
        <Route path=":productId">
          <Route index element={<OrganisationProducts />} />
          <Route path="sku/:skuId" element={<OrganisationProducts />} />
        </Route>
      </Route>

      <Route path="bookingtypes">
        <Route index element={<BookingTypesPage />} />
        <Route path="legacy/*" element={<OrganisationBooking />} />
      </Route>

      <Route path="promo-codes">
        <Route index element={<OrganisationPromoCodes />} />
        <Route path=":id" element={<OrganisationPromoCodes />} />
      </Route>

      <Route
        path="places"
        element={
          <RoleGuard
            allowedRoles={['Administrator', 'Manager']}
            redirectTo="/dashboard/organisation"
          />
        }
      >
        <Route index element={<PlacesPage />} />
        <Route path="legacy">
          <Route index element={<OrganisationPlaces />} />
          <Route path=":buildingId" element={<OrganisationPlaces />} />
        </Route>
        <Route path=":buildingId" element={<PlaceDetailsPage />} />
      </Route>
    </Route>

    {/* @ts-ignore – this route is old component without TS */}
    <Route path="upselling/:type" element={<Upselling />} />
    <Route path="integrations" element={<ProtectedIntegrations />} />
    <Route path="resident-events" element={<ProtectedResidentEvents />} />
    <Route path="customer-portal" element={<ProtectedCustomerPortal />} />
    <Route path="app-download" element={<AppDownloadPage />} />
    <Route
      element={<RoleGuard allowedRoles={['Administrator']} redirectTo="/dashboard/calendar" />}
    >
      <Route path="beta" element={<BetaPage />} />
    </Route>
  </Route>
)

// Note: kept out of /dashboard on purpose - the Reservations redesign moved to its own
// top-level route instead of the shared /dashboard prefix. Reuses the same Protect+Dashboard
// shell (navbar, auth guard) as every /dashboard route, just mounted at a different prefix -
// then nests the same ReservationsPage tab shell (header/tabs) the route had before the move.
// TODO: remove the production guard once the new Reservations pages launch. Until then they are
// reachable in production by Administrators only (via the Beta page); everyone else is sent to
// the legacy list.
export const reservationsRoutesConfig = () => (
  <Route path="/reservations" element={<Protect component={Dashboard} />}>
    <Route element={<NewReservationsGuard />}>
      <Route element={<ProtectedReservationsPage />}>
        <Route index element={<Navigate replace to="bookings" />} />
        <Route path="bookings" element={<ReservationsDashboardTab />} />
        <Route path="time-blocks" element={<TimeBlocksTab />} />
        <Route path="amenities" element={<ProtectedAmenitiesTab />} />
      </Route>
      {/* Note: sibling to the tab-shell above, not nested in it - the full-page detail view
    has its own header (back button), not the Reservations/Time blocks/Amenities tab bar. */}
      <Route path=":reservationId" element={<ReservationDetailsPage />} />
    </Route>
  </Route>
)
